import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Autocomplete,
  Stack,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircleOutline as ApproveIcon,
  DoNotDisturb as CloseIcon,
  FileDownload as ExportIcon,
  Cancel as CancelIcon,
  PictureAsPdf as PdfIcon,
  Email as EmailIcon,
  Print as PrintIcon,
  LocalShipping as DeliveryIcon,
  ReceiptLong as InvoiceIcon,
} from '@mui/icons-material';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import {
  fetchSalesOrders,
  fetchSalesOrder,
  createSalesOrder,
  updateSalesOrder,
  deleteSalesOrder,
  approveSalesOrder,
  closeSalesOrder,
  cancelSalesOrder,
  clearSelectedOrder,
} from '../store/slices/salesOrderSlice';
import { fetchCustomers } from '../store/slices/customerSlice';
import { fetchItems } from '../store/slices/itemSlice';
import { fetchWarehouses } from '../store/slices/warehouseSlice';
import { apiSuccess, apiError } from '../utils/toast';
import { generateSalesOrderPdf } from '../utils/pdfSalesOrder';
import salesOrderApi from '../services/salesOrderApi';
import quotationApi from '../services/quotationApi';
import PdfViewer from '../components/PdfViewer';
import QuickCreate from '../components/QuickCreate/QuickCreate';
import itemApi from '../services/itemApi';
import { createFilterOptions } from '@mui/material/Autocomplete';

const statusColors = {
  draft: 'default',
  approved: 'info',
  partially_delivered: 'warning',
  delivered: 'success',
  closed: 'secondary',
  cancelled: 'error',
};

// Option label: item name + variant attribute values, with on-hand stock.
const sellableOptionLabel = (opt) => {
  if (!opt) return '';
  const base = [opt.itemName, ...(opt.attributeValues || [])].filter(Boolean).join(' · ') || opt.itemName || '';
  return Number.isFinite(opt.availableStock) ? `${base} — ${opt.availableStock} available` : base;
};

// Keep searching by item code / SKU even though only the name is displayed.
const filterSellableOptions = createFilterOptions({
  stringify: (opt) => [
    opt.itemName, opt.itemCode, opt.variantSku, ...(opt.attributeValues || []),
  ].filter(Boolean).join(' '),
});

const SalesOrders = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { list, total, page, limit, selectedOrder, loading, submitting } = useSelector((s) => s.salesOrders);
  const { customers: customersList } = useSelector((s) => s.customers);
  const { warehouses: warehouseList } = useSelector((s) => s.warehouses);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfFilename, setPdfFilename] = useState('');
  const [pdfOrderId, setPdfOrderId] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sourceQuotationId, setSourceQuotationId] = useState(null);
  const [sourceQuotationNumber, setSourceQuotationNumber] = useState('');
  const [quotationDetailIds, setQuotationDetailIds] = useState([]);
  const [reasonDialog, setReasonDialog] = useState({ open: false, action: null, target: null });
  const [reasonText, setReasonText] = useState('');
  const [sellableOptions, setSellableOptions] = useState([]);
  const activeCompany = useSelector((s) => s.company?.activeCompany || null);

  const { register, handleSubmit, control, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      customerId: '',
      orderDate: new Date().toISOString().split('T')[0],
      warehouseId: '',
      reference: '',
      notes: '',
      status: 'draft',
      details: [{ itemId: '', description: '', quantity: 1, unitPrice: 0, taxPercentage: 0, discountPercentage: 0 }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'details' });

  const loadData = useCallback(() => {
    const params = { search, status: statusFilter, customerId: customerFilter, page, limit };
    dispatch(fetchSalesOrders(params));
  }, [dispatch, search, statusFilter, customerFilter, page, limit]);

  useEffect(() => {
    loadData();
    dispatch(fetchCustomers({ limit: 999 }));
    dispatch(fetchItems({ limit: 999 }));
    dispatch(fetchWarehouses({ limit: 999 }));
  }, [loadData, dispatch]);

  useEffect(() => {
    if (id && (id === 'new' || id === ':id')) return;
    if (id && openForm) {
      dispatch(fetchSalesOrder(id));
    }
  }, [id, openForm, dispatch]);

  useEffect(() => {
    if (selectedOrder && openForm && editId) {
      reset({
        customerId: selectedOrder.customerId || '',
        orderDate: selectedOrder.orderDate?.split('T')[0] || '',
        warehouseId: selectedOrder.warehouseId || '',
        reference: selectedOrder.reference || '',
        notes: selectedOrder.notes || '',
        status: selectedOrder.status || 'draft',
        details: selectedOrder.details?.length ? selectedOrder.details.map((d) => ({
          itemId: d.itemId || '',
          variantId: d.variantId || '',
          variantDescription: d.variantDescription || '',
          description: d.description || '',
          quantity: d.quantity || 0,
          unitPrice: d.unitPrice || 0,
          taxPercentage: d.taxPercentage || 0,
          discountPercentage: d.discountPercentage || 0,
        })) : [{ itemId: '', description: '', quantity: 1, unitPrice: 0, taxPercentage: 0, discountPercentage: 0 }],
      });
    }
  }, [selectedOrder, openForm, editId, reset]);

  const handleAdd = () => {
    setViewMode(false);
    setEditId(null);
    setSourceQuotationId(null);
    setSourceQuotationNumber('');
    setQuotationDetailIds([]);
    dispatch(clearSelectedOrder());
    reset({
      customerId: '',
      orderDate: new Date().toISOString().split('T')[0],
      warehouseId: '',
      reference: '',
      notes: '',
      status: 'draft',
      details: [{ itemId: '', description: '', quantity: 1, unitPrice: 0, taxPercentage: 0, discountPercentage: 0 }],
    });
    setOpenForm(true);
  };

  // Prefill new order from a quotation (navigate ?quotationId=X)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const quotationId = params.get('quotationId');
    if (!quotationId) return;
    (async () => {
      try {
        const res = await quotationApi.getConvertibleLines(quotationId);
        const data = res.data?.data || res.data;
        const lines = (data.lines || []).filter((l) => l.availableQuantity > 0);
        setViewMode(false);
        setEditId(null);
        setSourceQuotationId(data.id);
        setSourceQuotationNumber(data.quotationNumber);
        setQuotationDetailIds(lines.map((l) => l.quotationDetailId));
        reset({
          customerId: data.customerId || '',
          orderDate: new Date().toISOString().split('T')[0],
          warehouseId: data.warehouseId || '',
          reference: '',
          notes: data.quotationNumber ? `Converted from Quotation ${data.quotationNumber}` : '',
          status: 'draft',
          details: lines.map((l) => ({
            itemId: l.itemId,
            description: l.description || '',
            quantity: l.availableQuantity,
            unitPrice: l.unitPrice,
            taxPercentage: l.taxPercentage,
            discountPercentage: l.discountPercentage,
          })),
        });
        setOpenForm(true);
      } catch (e) {
        apiError('Could not load quotation for conversion');
      }
    })();
  }, [location.search]);

  const handleEdit = (order) => {
    setViewMode(false);
    setEditId(order.id);
    dispatch(fetchSalesOrder(order.id));
    setOpenForm(true);
  };

  const handleView = (order) => {
    setViewMode(true);
    setEditId(order.id);
    dispatch(fetchSalesOrder(order.id));
    setOpenForm(true);
  };

  const handleViewPdf = async (order) => {
    try {
      const response = await salesOrderApi.getById(order.id);
      const orderDetail = response.data?.data || response.data || response;
      const companyInfo = {
        name: activeCompany?.name || 'EzeeFlo ERP',
        address: activeCompany?.address || '',
        phone: activeCompany?.phone || '',
        email: activeCompany?.email || '',
        logo: activeCompany?.logo || null,
        currencyCode: activeCompany?.currencyCode || 'AED',
        trnTin: activeCompany?.trnTin || '',
      };
      const { blobUrl, pdfBlob, filename } = await generateSalesOrderPdf(orderDetail, companyInfo);
      setPdfBlobUrl(blobUrl);
      setPdfFilename(filename);
      setPdfOrderId(order.id);
      setPdfPreviewOpen(true);
    } catch (error) {
      apiError(error.response?.data?.message || error.message || 'Failed to generate PDF');
    }
  };

  const handleClosePdfPreview = () => {
    setPdfPreviewOpen(false);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setPdfFilename('');
    setPdfOrderId(null);
  };

  const handlePrintPdf = () => {
    if (!pdfBlobUrl) return;
    const printWindow = window.open(pdfBlobUrl, '_blank');
    if (printWindow) {
      printWindow.addEventListener('load', () => printWindow.print());
    }
  };

  const blobUrlToBase64 = async (blobUrl) => {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleOpenEmailDialog = () => {
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailTo) {
      apiError('Please enter recipient email address');
      return;
    }
    if (!pdfBlobUrl || !pdfOrderId) {
      apiError('PDF not ready. Please generate the PDF first.');
      return;
    }
    setSendingEmail(true);
    try {
      const pdfBase64 = await blobUrlToBase64(pdfBlobUrl);
      const response = await salesOrderApi.sendEmail(pdfOrderId, {
        to: emailTo,
        subject: emailSubject || undefined,
        body: emailBody || undefined,
        pdfBase64,
      });
      if (response.success) {
        apiSuccess('Sales Order sent successfully');
        setEmailDialogOpen(false);
        setEmailTo('');
        setEmailSubject('');
        setEmailBody('');
      }
    } catch (error) {
      apiError(error.response?.data?.message || error.message || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDelete = (order) => {
    if (order.status !== 'draft') {
      apiError('Only draft sales orders can be deleted');
      return;
    }
    setReasonDialog({ open: true, action: 'delete', target: order.id });
    setReasonText('');
  };

  const handleCancel = (order) => {
    setReasonDialog({ open: true, action: 'cancel', target: order.id });
    setReasonText('');
  };

  const handleReasonConfirm = async () => {
    const { action, target } = reasonDialog;
    setReasonDialog({ open: false, action: null, target: null });
    if (!target) return;
    if (action === 'delete') {
      const result = await dispatch(deleteSalesOrder({ id: target, reason: reasonText || null }));
      if (result.meta.requestStatus === 'fulfilled') {
        apiSuccess('Sales Order deleted successfully');
      } else {
        apiError(result.payload || 'Failed to delete sales order');
      }
    } else if (action === 'cancel') {
      const result = await dispatch(cancelSalesOrder({ id: target, reason: reasonText || null }));
      if (result.meta.requestStatus === 'fulfilled') {
        apiSuccess('Sales Order cancelled successfully');
      } else {
        apiError(result.payload || 'Failed to cancel sales order');
      }
    }
    setReasonText('');
    loadData();
  };

  const handleApprove = (order) => {
    dispatch(approveSalesOrder(order.id)).then(() => loadData());
  };

  const handleClose = (order) => {
    dispatch(closeSalesOrder(order.id)).then(() => loadData());
  };

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      quotationId: sourceQuotationId || null,
      details: (data.details || []).map((d, i) => ({
        ...d,
        quotationDetailId: quotationDetailIds[i] || d.quotationDetailId || null,
      })),
    };
    if (editId) {
      await dispatch(updateSalesOrder({ id: editId, data: payload }));
    } else {
      await dispatch(createSalesOrder(payload));
    }
    setOpenForm(false);
    setEditId(null);
    setSourceQuotationId(null);
    setSourceQuotationNumber('');
    setQuotationDetailIds([]);
    dispatch(clearSelectedOrder());
    loadData();
  };

  const handleCloseDialog = () => {
    setOpenForm(false);
    setEditId(null);
    setViewMode(false);
  };

  const handlePageChange = (e, newPage) => {
    const params = { search, status: statusFilter, customerId: customerFilter, page: newPage + 1, limit };
    dispatch(fetchSalesOrders(params));
  };

  const handleRowsPerPageChange = (e) => {
    const params = { search, status: statusFilter, customerId: customerFilter, page: 1, limit: parseInt(e.target.value) };
    dispatch(fetchSalesOrders(params));
  };

  const details = watch('details');
  const selectedWarehouseId = watch('warehouseId');

  const loadSellableOptions = useCallback(async (whId) => {
    try {
      const res = await itemApi.getSellableOptions(whId ? { warehouseId: whId } : {});
      const list = res.data || [];
      setSellableOptions(list);
      return list;
    } catch (e) {
      return [];
    }
  }, []);

  useEffect(() => {
    if (!openForm) return;
    loadSellableOptions(selectedWarehouseId);
  }, [openForm, selectedWarehouseId, loadSellableOptions]);

  const optionForLine = (line) => {
    if (!line || !line.itemId) return null;
    return sellableOptions.find(
      (o) => o.itemId === line.itemId && (o.variantId || '') === (line.variantId || '')
    ) || null;
  };

  const maxStockForLine = (line) => {
    if (!line) return null;
    const opt = optionForLine(line);
    if (!opt || opt.itemType !== 'product' || !opt.isInventoryTracked) return null;
    return Number.isFinite(opt.availableStock) ? opt.availableStock : null;
  };

  const handleItemSelect = (index, opt) => {
    if (!opt) {
      setValue(`details.${index}.variantId`, '');
      setValue(`details.${index}.variantDescription`, '');
      return;
    }
    setValue(`details.${index}.itemId`, opt.itemId);
    setValue(`details.${index}.variantId`, opt.variantId || '');
    setValue(`details.${index}.variantDescription`, opt.variantDescription || '');
    setValue(`details.${index}.description`, opt.variantDescription || opt.itemName || '');
    setValue(`details.${index}.unitPrice`, opt.sellingPrice || 0);
    setValue(`details.${index}.taxPercentage`, opt.taxPercentage || 0);
    if (Number.isFinite(opt.availableStock)) {
      const qty = parseFloat(details?.[index]?.quantity);
      if (Number.isFinite(qty) && qty > opt.availableStock) {
        setValue(`details.${index}.quantity`, opt.availableStock);
      }
    }
  };

  const calculateLineTotal = (line) => {
    const qty = parseFloat(line.quantity) || 0;
    const price = parseFloat(line.unitPrice) || 0;
    const tax = parseFloat(line.taxPercentage) || 0;
    const disc = parseFloat(line.discountPercentage) || 0;
    const gross = qty * price;
    const discAmount = gross * (disc / 100);
    const taxAmount = (gross - discAmount) * (tax / 100);
    return (gross - discAmount + taxAmount).toFixed(2);
  };

  const calculateTotals = () => {
    if (!details) return { subtotal: 0, taxAmount: 0, discountAmount: 0, total: 0 };
    let sub = 0, tax = 0, disc = 0;
    details.forEach((line) => {
      const qty = parseFloat(line.quantity) || 0;
      const price = parseFloat(line.unitPrice) || 0;
      const taxPct = parseFloat(line.taxPercentage) || 0;
      const discPct = parseFloat(line.discountPercentage) || 0;
      const gross = qty * price;
      const discAmt = gross * (discPct / 100);
      const taxAmt = (gross - discAmt) * (taxPct / 100);
      sub += gross;
      disc += discAmt;
      tax += taxAmt;
    });
    return {
      subtotal: sub.toFixed(2),
      discountAmount: disc.toFixed(2),
      taxAmount: tax.toFixed(2),
      total: (sub - disc + tax).toFixed(2),
    };
  };

  const totals = calculateTotals();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700}>Sales Orders</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          New Sales Order
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search orders..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField select fullWidth size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="partially_delivered">Partially Delivered</MenuItem>
              <MenuItem value="delivered">Delivered</MenuItem>
              <MenuItem value="partially_invoiced">Partially Invoiced</MenuItem>
              <MenuItem value="fully_invoiced">Fully Invoiced</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <IconButton onClick={loadData}><RefreshIcon /></IconButton>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Order #</strong></TableCell>
              <TableCell><strong>Customer</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Total</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center">Loading...</TableCell></TableRow>
            ) : list.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center">No sales orders found</TableCell></TableRow>
            ) : list.map((order) => (
              <TableRow key={order.id} hover>
                <TableCell>{order.orderNumber}</TableCell>
                <TableCell>{order.customer?.name}</TableCell>
                <TableCell>{order.orderDate?.split('T')[0]}</TableCell>
                <TableCell>{parseFloat(order.totalAmount).toFixed(2)}</TableCell>
                <TableCell>
                  <Chip label={(order.status || '').replace('_', ' ').toUpperCase()} color={statusColors[order.status] || 'default'} size="small" />
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title="View"><IconButton size="small" onClick={() => handleView(order)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="View as PDF"><IconButton size="small" color="primary" onClick={() => handleViewPdf(order)}><PdfIcon fontSize="small" /></IconButton></Tooltip>
                    {(
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => handleEdit(order)} disabled={order.status !== 'draft'}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                    {order.status === 'draft' && (
                      <Tooltip title="Approve"><IconButton size="small" color="success" onClick={() => handleApprove(order)}><ApproveIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                    {['approved', 'partially_delivered'].includes(order.status) && (
                      <Tooltip title="Close"><IconButton size="small" color="warning" onClick={() => handleClose(order)}><CloseIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                    {['approved', 'confirmed', 'partially_delivered', 'delivered', 'partially_invoiced'].includes(order.status) && (
                      <Tooltip title="Create Delivery Note"><IconButton size="small" color="info" onClick={() => navigate(`/app/sales/delivery-notes/new?salesOrderId=${order.id}`)}><DeliveryIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                    {['approved', 'confirmed', 'partially_delivered', 'delivered', 'partially_invoiced'].includes(order.status) && (
                      <Tooltip title="Create Invoice"><IconButton size="small" color="secondary" onClick={() => navigate(`/app/sales/invoices/new?salesOrderId=${order.id}`)}><InvoiceIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                    {['draft', 'approved', 'confirmed', 'partially_delivered', 'partially_invoiced'].includes(order.status) && (
                      <Tooltip title="Cancel"><IconButton size="small" color="warning" onClick={() => handleCancel(order)}><CancelIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                    {(
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(order)} disabled={order.status !== 'draft'}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={limit}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </TableContainer>

      {/* Create/Edit/View Dialog */}
      <Dialog open={openForm} onClose={handleCloseDialog} fullWidth maxWidth="lg">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {viewMode ? 'View Sales Order' : editId ? 'Edit Sales Order' : 'New Sales Order'}
            {selectedOrder?.orderNumber && (
              <Chip label={selectedOrder.orderNumber} sx={{ ml: 2 }} />
            )}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Controller
                    name="customerId"
                    control={control}
                    rules={{ required: 'Customer is required' }}
                    render={({ field }) => (
                      <Autocomplete
                        options={customersList}
                        getOptionLabel={(opt) => `${opt.itemCode || opt.code} - ${opt.name}`}
                        value={customersList.find((c) => c.id === field.value) || null}
                        onChange={(e, val) => field.onChange(val?.id || '')}
                        renderInput={(params) => (
                          <TextField {...params} label="Customer" error={!!errors.customerId} helperText={errors.customerId?.message} fullWidth disabled={viewMode} />
                        )}
                        disabled={viewMode}
                        sx={{ flex: 1 }}
                      />
                    )}
                  />
                  <QuickCreate
                    entityKey="customer"
                    disabled={viewMode}
                    onCreated={(c) => {
                      dispatch(fetchCustomers({ limit: 999 }));
                      setValue('customerId', c.id, { shouldValidate: true });
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField label="Order Date" type="date" fullWidth InputLabelProps={{ shrink: true }} {...register('orderDate', { required: true })} disabled={viewMode} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Controller
                  name="warehouseId"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={warehouseList}
                      getOptionLabel={(opt) => opt.name}
                      value={warehouseList.find((w) => w.id === field.value) || null}
                      onChange={(e, val) => field.onChange(val?.id || '')}
                      renderInput={(params) => <TextField {...params} label="Warehouse" fullWidth />}
                      disabled={viewMode}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField label="Reference" fullWidth {...register('reference')} disabled={viewMode} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField select label="Status" fullWidth {...register('status')} disabled={viewMode || !editId}>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="partially_delivered">Partially Delivered</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" fontWeight={600} mb={1}>Line Items</Typography>
              </Grid>
              <Grid item xs={12}>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width="32%">Item</TableCell>
                        <TableCell width="17%">Description</TableCell>
                        <TableCell width="8%">Qty</TableCell>
                        <TableCell width="14%">Unit Price</TableCell>
                        <TableCell width="8%">Tax %</TableCell>
                        <TableCell width="8%">Disc %</TableCell>
                        <TableCell width="9%">Total</TableCell>
                        {!viewMode && <TableCell width="4%"></TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields.map((field, index) => (
                        <TableRow key={field.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Controller
                                name={`details.${index}.itemId`}
                                control={control}
                                rules={{ required: 'Item is required' }}
                                render={({ field: f }) => (
                                  <Autocomplete
                                    size="small"
                                    options={sellableOptions}
                                    getOptionLabel={sellableOptionLabel}
                                    isOptionEqualToValue={(opt, val) => opt.key === val.key}
                                    filterOptions={filterSellableOptions}
                                    renderOption={(props, option) => (
                                      <li {...props} key={option.key}>{sellableOptionLabel(option)}</li>
                                    )}
                                    value={sellableOptions.find((o) => o.itemId === f.value && (o.variantId || '') === (details[index]?.variantId || '')) || null}
                                    onChange={(e, val) => {
                                      f.onChange(val?.itemId || '');
                                      handleItemSelect(index, val);
                                    }}
                                    renderInput={(params) => (
                                      <TextField {...params} placeholder="Select Item" error={!!errors.details?.[index]?.itemId} helperText={errors.details?.[index]?.itemId?.message} />
                                    )}
                                    disabled={viewMode}
                                    sx={{ flex: 1, minWidth: 320 }}
                                  />
                                )}
                              />
                              <QuickCreate
                                entityKey="item"
                                disabled={viewMode}
                                onCreated={async (it) => {
                                  const list = await loadSellableOptions(selectedWarehouseId);
                                  const opt = list.find((o) => o.itemId === it.id && !o.variantId)
                                    || list.find((o) => o.itemId === it.id);
                                  if (opt) handleItemSelect(index, opt);
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <TextField size="small" fullWidth {...register(`details.${index}.description`)} disabled={viewMode} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" type="number" fullWidth
                              {...register(`details.${index}.quantity`, {
                                required: true,
                                valueAsNumber: true,
                                validate: (value) => {
                                  const max = maxStockForLine(details[index]);
                                  if (max === null) return true;
                                  return !Number.isFinite(value) || value <= max || `Only ${max} available`;
                                },
                              })}
                              onChange={(e) => {
                                const max = maxStockForLine(details[index]);
                                let raw = e.target.value;
                                if (max !== null && raw !== '' && parseFloat(raw) > max) raw = String(max);
                                setValue(`details.${index}.quantity`, raw === '' ? '' : Number(raw), { shouldValidate: true, shouldDirty: true });
                              }}
                              error={!!errors.details?.[index]?.quantity}
                              helperText={errors.details?.[index]?.quantity?.message}
                              disabled={viewMode}
                              inputProps={{ min: 0, step: 1, max: maxStockForLine(details[index]) ?? undefined }} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" type="number" fullWidth {...register(`details.${index}.unitPrice`, { required: true, valueAsNumber: true })} disabled={viewMode} inputProps={{ min: 0, step: 0.01 }} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" type="number" fullWidth {...register(`details.${index}.taxPercentage`, { valueAsNumber: true })} disabled={viewMode} inputProps={{ min: 0, max: 100, step: 0.01 }} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" type="number" fullWidth {...register(`details.${index}.discountPercentage`, { valueAsNumber: true })} disabled={viewMode} inputProps={{ min: 0, max: 100, step: 0.01 }} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{calculateLineTotal(watch(`details.${index}`) || {})}</Typography>
                          </TableCell>
                          {!viewMode && (
                            <TableCell>
                              <IconButton size="small" color="error" onClick={() => remove(index)} disabled={fields.length <= 1}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {!viewMode && (
                  <Button size="small" sx={{ mt: 1 }} onClick={() => append({ itemId: '', description: '', quantity: 1, unitPrice: 0, taxPercentage: 0, discountPercentage: 0 })}>
                    + Add Line
                  </Button>
                )}
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end">
                  <Stack spacing={0.5} textAlign="right">
                    <Typography variant="body2">Subtotal: <strong>{totals.subtotal}</strong></Typography>
                    <Typography variant="body2">Discount: <strong>{totals.discountAmount}</strong></Typography>
                    <Typography variant="body2">Tax: <strong>{totals.taxAmount}</strong></Typography>
                    <Typography variant="h6">Total: <strong>{totals.total}</strong></Typography>
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField label="Notes" fullWidth multiline rows={2} {...register('notes')} disabled={viewMode} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            {!viewMode && (
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? 'Saving...' : editId ? 'Update' : 'Create'}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={pdfPreviewOpen} onClose={handleClosePdfPreview} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Sales Order Preview - {pdfFilename.replace('SalesOrder_', '').replace('.pdf', '')}</Typography>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Send Email"><IconButton size="small" color="primary" onClick={handleOpenEmailDialog}><EmailIcon /></IconButton></Tooltip>
              <Tooltip title="Print"><IconButton size="small" color="secondary" onClick={handlePrintPdf}><PrintIcon /></IconButton></Tooltip>
              <Tooltip title="Download PDF"><IconButton size="small" color="primary" onClick={() => {
                if (pdfBlobUrl) { const a = document.createElement('a'); a.href = pdfBlobUrl; a.download = pdfFilename; a.click(); }
              }}>
                <ExportIcon />
              </IconButton></Tooltip>
              <IconButton size="small" onClick={handleClosePdfPreview}><CancelIcon /></IconButton>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '80vh', p: 0, overflow: 'hidden' }}>
          {pdfBlobUrl && <PdfViewer blobUrl={pdfBlobUrl} />}
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <EmailIcon color="primary" />
            <Typography variant="h6">Send Sales Order via Email</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField fullWidth label="Recipient Email" type="email" required value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)} placeholder="customer@example.com" size="small" />
            <TextField fullWidth label="Subject" value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder={`Sales Order #${pdfFilename.replace('SalesOrder_', '').replace('.pdf', '')}`} size="small" />
            <TextField fullWidth label="Email Body" multiline rows={8} value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              placeholder={`Dear Customer,\n\nPlease find attached the sales order for your reference.\n\nBest regards,\n${activeCompany?.name || 'EzeeFlo ERP'}`} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)} disabled={sendingEmail}>Cancel</Button>
          <Button onClick={handleSendEmail} variant="contained" color="primary"
            disabled={sendingEmail || !emailTo}
            startIcon={sendingEmail ? <CircularProgress size={16} /> : <EmailIcon />}>
            {sendingEmail ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reason Dialog for Delete/Cancel */}
      <Dialog open={reasonDialog.open} onClose={() => setReasonDialog({ open: false, action: null, target: null })} fullWidth maxWidth="sm">
        <DialogTitle>{reasonDialog.action === 'delete' ? 'Delete Sales Order' : 'Cancel Sales Order'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Reason (optional)"
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonDialog({ open: false, action: null, target: null })}>Back</Button>
          <Button variant="contained" color={reasonDialog.action === 'delete' ? 'error' : 'warning'} onClick={handleReasonConfirm}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesOrders;